import classNames from 'classnames';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FlexSpacer } from '@insights-gaming/material-components';
import { AttackIcon, BaronIcon, DragonIcon, GoldIcon, TowerIcon } from '@insights-gaming/material-icons';
import { Theme } from '@insights-gaming/theme';
import { SvgIcon } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';

import { Riot } from '../../../types';
import { LoLHelper } from '../../..';

interface LeagueOfLegendsTeamOverviewOwnProps {
  className?: string;
  teamStats: Riot.League.RawDataInfoTeam;
  participantStats: Riot.League.RawDataInfoParticipant[];
  isUserTeam: boolean;
}

type LeagueOfLegendsTeamOverviewProps = LeagueOfLegendsTeamOverviewOwnProps;

const useStyles = makeStyles((theme: Theme) => createStyles(
  {
  root: {
    display: 'grid',
    alignItems: 'center',
    gridTemplateColumns: 'repeat(6, 1fr)',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0, 1),
  },
  teamStatsData: {
    flex: 1,
  },
  gameResult: {
    color: ({ isUserTeam }: LeagueOfLegendsTeamOverviewProps) => LoLHelper.getUserColor(theme, isUserTeam),
  },
  icon: {
    color: ({ isUserTeam }: LeagueOfLegendsTeamOverviewProps) => LoLHelper.getUserColor(theme, isUserTeam),
  },
}), {name: 'LeagueOfLegendsTeamOverview'});

export function LeagueOfLegendsTeamOverview(props: LeagueOfLegendsTeamOverviewProps) {
  const classes = useStyles(props);
  const { className, teamStats, participantStats } = props;

  const { i18n } = useTranslation();

  const totalGold = useMemo(() => {
    let gold = 0;
    for (const participant of participantStats) {
      gold += participant.goldEarned;
    }
    return gold.toLocaleString(i18n.language);
  }, [participantStats, i18n]);

  const totalKills = useMemo(() => {
    let kills = 0;
    for (const participant of participantStats) {
      kills += participant.kills;
    }
    return kills;
  }, [participantStats]);

  const teamStatsData = useMemo(() => {
    return [
      {
        key: 'totalKills',
        icon: <AttackIcon />,
        data: totalKills,
      },
      {
        key: 'totalGold',
        icon: <GoldIcon />,
        data: totalGold,
      },
      {
        key: 'baron',
        icon: <BaronIcon />,
        data: teamStats.objectives.baron.kills,
      },
      {
        key: 'dragon',
        icon: <DragonIcon />,
        data: teamStats.objectives.dragon.kills,
      },
      {
        key: 'tower',
        icon: <TowerIcon />,
        data: teamStats.objectives.tower.kills,
      },
    ];
  }, [teamStats, totalKills, totalGold]);

  return (
    <div className={classNames(classes.root, className)}>
      <div className={classes.gameResult}>
        {teamStats.win ? (
          <span>Victory</span>
        ) : (
          <span>Defeat</span>
        )}
      </div>
      {teamStatsData.map((d) => {
        return (
          <FlexSpacer key={d.key} flexAlignItems='center' spacing={0}>
            <SvgIcon className={classes.icon}>
              {d.icon}
            </SvgIcon>
            <div className={classes.teamStatsData}>
              {d.data}
            </div>
          </FlexSpacer>
        );
      })}
    </div>
  );
}
